import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

export default function AdminMenu() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/settings").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const menu = data.nav_menu || [];
  const setMenu = (arr) => setData({ ...data, nav_menu: arr });

  const update = (i, field, val) => setMenu(menu.map((m, idx) => idx === i ? { ...m, [field]: val } : m));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= menu.length) return;
    const arr = [...menu];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setMenu(arr);
  };
  const add = () => setMenu([...menu, { label: "New Item", to: "/", visible: true }]);
  const remove = (i) => setMenu(menu.filter((_, idx) => idx !== i));

  const save = async () => {
    const cleaned = menu.filter((m) => (m.label || "").trim() && (m.to || "").trim());
    try { await api.put("/settings", { data: { ...data, nav_menu: cleaned } }); toast.success("Menu saved"); refresh(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="p-8 pb-24 max-w-3xl" data-testid="admin-menu">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-4">
        <h1 className="font-display font-black text-3xl">Website Navigation</h1>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="menu-save"><Save className="w-4 h-4" /> Save</button>
      </div>
      <p className="text-sm text-neutral-500 mb-6">Add, edit, reorder, show/hide or delete the main website menu items. Use a path like <code>/partner-with-us</code> or a full URL.</p>

      <div className="space-y-3">
        {menu.map((m, i) => (
          <div key={i} className={`bg-white border rounded-2xl p-4 flex items-center gap-3 flex-wrap ${m.visible === false ? "border-neutral-200 opacity-60" : "border-neutral-200"}`} data-testid={`menu-item-${i}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-neutral-500 hover:text-neutral-900 disabled:opacity-30" data-testid={`menu-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === menu.length - 1} className="p-1 text-neutral-500 hover:text-neutral-900 disabled:opacity-30" data-testid={`menu-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
            </div>
            <input value={m.label || ""} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label"
                   className="flex-1 min-w-[120px] px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`menu-label-${i}`} />
            <input value={m.to || ""} onChange={(e) => update(i, "to", e.target.value)} placeholder="/path or URL"
                   className="flex-1 min-w-[140px] px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`menu-to-${i}`} />
            <button onClick={() => update(i, "visible", m.visible === false)} title={m.visible === false ? "Hidden" : "Visible"}
                    className={`p-2 rounded-lg border ${m.visible === false ? "border-neutral-300 text-neutral-400" : "border-emerald-200 text-emerald-600"}`} data-testid={`menu-visible-${i}`}>
              {m.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button onClick={() => remove(i)} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`menu-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <button onClick={add} className="btn-outline-eq text-sm mt-4" data-testid="menu-add"><Plus className="w-4 h-4" /> Add menu item</button>
    </div>
  );
}
